import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { scaleIn, staggerContainer, fadeInUp } from '../utils/animations';
import './AuthPages.css';

function RegisterPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    smsConsent: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (formData.phone && !formData.smsConsent) {
      setError('Please consent to SMS to use this phone number');
      return;
    }

    setLoading(true);

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      phone: formData.phone || undefined,
      smsConsent: formData.smsConsent,
    };

    const result = await register(payload);

    if (result.success) {
      setSuccess('Account created. Check your email for verification instructions.');
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1800);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <motion.div
          className="auth-card"
          variants={scaleIn}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={staggerContainer} initial="hidden" animate="visible">
            <motion.h1 className="auth-title" variants={fadeInUp}>Create Account</motion.h1>
            <motion.p className="auth-subtitle" variants={fadeInUp}>Fast sign up. Edit profile details any time later.</motion.p>

            <AnimatePresence>
              {error && (
                <motion.div
                  className="auth-error"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  {error}
                </motion.div>
              )}
              {success && (
                <motion.div
                  className="auth-success"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  {success}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.form onSubmit={handleSubmit} className="auth-form" variants={fadeInUp}>
              <div className="form-row">
                <div className="form-group form-group-floating">
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder=" "
                  />
                  <label htmlFor="firstName">First Name</label>
                </div>

                <div className="form-group form-group-floating">
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder=" "
                  />
                  <label htmlFor="lastName">Last Name</label>
                </div>
              </div>

              <div className="form-group form-group-floating">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder=" "
                />
                <label htmlFor="email">Email</label>
                <small style={{ color: '#999', fontSize: '0.85rem' }}>
                  Used for login and notifications. Username is auto-generated.
                </small>
              </div>

              <div className="form-row">
                <div className="form-group form-group-floating">
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder=" "
                  />
                  <label htmlFor="password">Password</label>
                </div>

                <div className="form-group form-group-floating">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder=" "
                  />
                  <label htmlFor="confirmPassword">Confirm Password</label>
                </div>
              </div>

              <div className="form-group form-group-floating">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder=" "
                />
                <label htmlFor="phone">Phone (Optional)</label>
              </div>

              {formData.phone && (
                <div className="form-group" style={{ marginTop: '0.25rem' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', fontSize: '0.95rem' }}>
                    <input
                      type="checkbox"
                      name="smsConsent"
                      checked={formData.smsConsent}
                      onChange={handleChange}
                      required
                      style={{ marginRight: '0.75rem', marginTop: '0.25rem', cursor: 'pointer' }}
                    />
                    <span style={{ lineHeight: '1.45', color: '#ddd' }}>
                      I agree to receive transactional SMS reminders and booking updates. Reply STOP to opt out.
                    </span>
                  </label>
                </div>
              )}

              <button type="submit" className="auth-button" disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </motion.form>

            <motion.div className="auth-footer" variants={fadeInUp}>
              <p>
                Already have an account?{' '}
                <Link to="/login" className="auth-link">
                  Login
                </Link>
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default RegisterPage;
